// Central content — Ricky Sokhi · Freelance UI/UX Designer & Front-end Developer

export const CONTACT = {
  email: "rickysokhi@gmail.com",
  phone: "+91-9888626566",
  linkedin: "https://www.linkedin.com/in/baljit-pal-singh",
  linkedinLabel: "linkedin.com/in/baljit-pal-singh",
  behance: "https://www.behance.net/rickysokhi",
  behanceLabel: "behance.net/rickysokhi",
  availability: "Available for freelance work",
};

export const PORTRAIT =
  "https://customer-assets-lqy194kg.emergentagent.net/job_outcome-focused-1/artifacts/04czly1n_rsn.jpg";

export const HERO = {
  headlineLines: [
    { segs: [{ t: "UI/UX designer" }] },
    { segs: [{ t: "& front-end developer" }] },
    { segs: [{ t: "crafting " }, { t: "soft, delightful", grad: true }] },
    { segs: [{ t: "digital experiences." }] },
  ],
  sub: "Freelance UI/UX designer & front-end developer with 15+ years crafting websites, web apps and brand experiences — now supercharged with an AI-powered workflow and a growth partner's eye for results.",
  bullets: [
    "Pixel-perfect UI/UX, from wireframe to polished interface.",
    "Hand-coded front-end: HTML, CSS, JavaScript — responsive & mobile-first.",
    "AI-accelerated delivery and a growth mindset focused on results.",
  ],
};

export const STATS = [
  { value: "15+", label: "years designing & building" },
  { value: "50+", label: "websites & apps shipped" },
  { value: "AI", label: "powered workflow" },
];

export const PILLARS = [
  {
    no: "01",
    title: "Design that feels effortless",
    body: "Clean, intuitive interfaces with a soft, modern aesthetic. I obsess over hierarchy, spacing and flow so every screen feels calm and easy to use.",
  },
  {
    no: "02",
    title: "Built pixel-perfect, by hand",
    body: "I don't just design — I code. Hand-written HTML, CSS and JavaScript that turn a design into a fast, responsive, cross-device reality.",
  },
  {
    no: "03",
    title: "Partnered for growth",
    body: "Beautiful isn't enough. I care about conversion, clarity and results, and I make design and product decisions that move your metrics.",
  },
];

export const SKILLS = [
  {
    icon: "PenTool",
    title: "UI / UX Design",
    desc: "Wireframing, prototyping, user flows and design systems — turning ideas and PSDs into intuitive, polished interfaces.",
    tags: ["Wireframes", "Prototyping", "Design systems", "Figma", "PSD → UI"],
  },
  {
    icon: "Code2",
    title: "Front-end Development",
    desc: "Hand-coded, standards-compliant front-ends with HTML, CSS, JavaScript and jQuery — clean, maintainable and W3C-valid.",
    tags: ["HTML", "CSS", "JavaScript", "jQuery", "Git"],
  },
  {
    icon: "Smartphone",
    title: "Responsive & Web Design",
    desc: "Mobile-first, cross-device layouts plus brand and graphic design — landing pages and sites that look sharp everywhere.",
    tags: ["Responsive", "Mobile", "Landing pages", "Branding", "Graphics"],
  },
  {
    icon: "Sparkles",
    title: "AI & Growth",
    desc: "An AI-accelerated workflow for faster delivery, plus conversion-focused thinking to help your product actually grow.",
    tags: ["AI workflow", "Conversion", "Analytics", "SaaS", "Consulting"],
  },
];

// Featured live websites — full case studies with real snapshots
export const PROJECTS = [
  {
    slug: "lecrans",
    title: "LeCrans — five-star Alpine resort & spa",
    clientType: "Luxury Hospitality",
    result: "An immersive, cinematic site for a 5-star Swiss mountain resort.",
    image: "/portfolio/site-lecrans.jpg",
    live: "https://www.lecrans.com",
    snapshot: {
      client: "LeCrans Hotel & Spa, Crans-Montana",
      sector: "Luxury Hospitality",
      duration: "Ongoing",
      role: "UI/UX & Front-end Developer",
      results: [
        "Cinematic, image-led experience worthy of a 5-star resort.",
        "Clear paths to rooms, dining, spa and reservations.",
        "Fully responsive across desktop, tablet and mobile.",
      ],
    },
    challenge:
      "LeCrans, a Relais & Châteaux five-star boutique resort in the Swiss Alps, needed a website as refined as the property itself — showcasing rooms, Michelin-starred dining and its wellness spa while making reservations effortless.",
    approach: [
      "Designed a full-bleed, image-led experience with elegant typography.",
      "Structured clear journeys for rooms & suites, dining, spa and offers.",
      "Integrated a prominent, friction-free reservation flow.",
      "Built responsive, performant front-end with multi-language support.",
    ],
    results: [
      "A luxurious digital presence that matches the brand.",
      "Effortless discovery of rooms, dining and wellness.",
      "Smooth booking journey on every device.",
    ],
  },
  {
    slug: "zoomrealty",
    title: "Zoom Realty — find your dream home",
    clientType: "Real Estate",
    result: "A fast, search-first real estate platform for US buyers & renters.",
    image: "/portfolio/site-zoomrealty.jpg",
    live: "https://www.zoomrealty.us",
    snapshot: {
      client: "Zoom Realty (US real estate)",
      sector: "Real Estate",
      duration: "8 weeks",
      role: "UI/UX & Front-end Developer",
      results: [
        "Search-first homepage with buy / sell / rent filters.",
        "Rich, scannable property listing cards.",
        "Responsive experience that speeds up discovery.",
      ],
    },
    challenge:
      "Zoom Realty needed an approachable, trustworthy home-search experience where buyers and renters could immediately filter by price, beds, baths and location — without wading through clutter.",
    approach: [
      "Designed a hero with an integrated, filter-ready property search.",
      "Built clean listing cards showing price, specs and status at a glance.",
      "Added clear buy / sell / rent journeys and agent contact CTAs.",
      "Delivered a responsive, fast-loading front-end.",
    ],
    results: [
      "Faster, clearer property discovery for visitors.",
      "More engagement with prominent, scannable listings.",
      "Stronger lead capture through focused CTAs.",
    ],
  },
  {
    slug: "uniqueverve",
    title: "Unique Verve — functional medicine & skincare",
    clientType: "Health & E-commerce",
    result: "A calming wellness brand site with booking and online store.",
    image: "/portfolio/site-uniqueverve.jpg",
    live: "https://www.uniqueverve.com",
    snapshot: {
      client: "Unique Verve, Boston",
      sector: "Wellness / E-commerce",
      duration: "7 weeks",
      role: "UI/UX & Front-end Developer",
      results: [
        "Soothing, premium brand identity for a wellness clinic.",
        "Integrated appointment booking and a skincare store.",
        "Loyalty/rewards and clear service explanations.",
      ],
    },
    challenge:
      "A Boston functional-medicine and medical-grade skincare practice needed a site that felt calm and premium, explained its services clearly, and let clients both book appointments and shop professional skincare online.",
    approach: [
      "Crafted a soft, wellness-led visual identity and layout.",
      "Structured services, treatments and practitioner credibility.",
      "Integrated appointment booking and an e-commerce product catalog.",
      "Added a rewards program and clear conversion paths.",
    ],
    results: [
      "A premium presence that builds patient trust.",
      "Simple booking and shopping in one place.",
      "Clear routes from browsing to booking or buying.",
    ],
  },
  {
    slug: "ustrendy",
    title: "USTrendy — independent fashion marketplace",
    clientType: "Fashion E-commerce",
    result: "A vibrant marketplace for shopping independent designers.",
    image: "/portfolio/site-ustrendy.jpg",
    live: "https://www.ustrendy.com",
    snapshot: {
      client: "USTrendy (fashion marketplace)",
      sector: "Fashion / E-commerce",
      duration: "Ongoing",
      role: "UI/UX & Front-end Developer",
      results: [
        "Bold, trend-forward storefront for indie fashion.",
        "Category and trend-led browsing that drives discovery.",
        "Responsive shopping experience end to end.",
      ],
    },
    challenge:
      "USTrendy connects shoppers with independent fashion designers worldwide and needed a lively, trend-forward storefront that made discovering and buying unique pieces effortless.",
    approach: [
      "Designed a bold, colourful storefront that celebrates the products.",
      "Built trend- and category-led browsing to aid discovery.",
      "Structured clean product and collection layouts.",
      "Delivered a responsive, shoppable front-end.",
    ],
    results: [
      "An energetic brand experience that fits the audience.",
      "Easier discovery of independent designers and trends.",
      "Smooth, mobile-friendly shopping journey.",
    ],
  },
  {
    slug: "isoduct",
    title: "Isoduct — premium chimney systems",
    clientType: "Industrial Product",
    result: "A sleek product-brand site with a dealer locator.",
    image: "/portfolio/site-isoduct.jpg",
    live: "https://www.isoduct.nl",
    snapshot: {
      client: "Isoduct® (Netherlands)",
      sector: "Industrial / Product",
      duration: "6 weeks",
      role: "UI/UX & Front-end Developer",
      results: [
        "Refined, image-led site for a premium chimney system.",
        "Clear technical product presentation.",
        "Dealer locator to drive qualified enquiries.",
      ],
    },
    challenge:
      "Isoduct, a premium Dutch chimney and flue system, needed a modern brand site that balanced striking visuals with clear technical detail — and helped visitors find an authorised dealer.",
    approach: [
      "Designed a clean, image-led layout that elevates the product.",
      "Presented technical specs and benefits in a digestible way.",
      "Built a dealer locator to convert interest into enquiries.",
      "Delivered a fast, responsive, multi-section front-end.",
    ],
    results: [
      "A premium presence that positions the product well.",
      "Clearer technical understanding for buyers.",
      "A direct path from interest to local dealers.",
    ],
  },
];

// Mobile apps — front-end / UI work (live on Google Play)
export const MOBILE_APPS = [
  {
    name: "Jewzz",
    tagline: "Dating & Matchmaking",
    icon: "/portfolio/app-jewzz.webp",
    initial: "J",
    meta: "Lifestyle · 5K+ downloads",
    desc: "A Jewish dating & events app born from the world-famous Matzoball® event — with swipe matching, video messaging, online speed dating and local events. I contributed to the UI and front-end experience.",
    url: "https://play.google.com/store/apps/details?id=com.matzoball.app",
  },
  {
    name: "Hookup",
    tagline: "Social Dating Platform",
    icon: null,
    initial: "H",
    meta: "Social · Google Play",
    desc: "A social dating platform focused on quick, authentic connections — profiles, matching and real-time chat, with a clean, mobile-first interface designed for engagement.",
    url: "https://play.google.com/store/search?q=hookup.com&c=apps",
  },
  {
    name: "Shoftek",
    tagline: "Social Platform",
    icon: null,
    initial: "S",
    meta: "Social · Google Play",
    desc: "A social platform for sharing and discovering content and connecting with a community — designed with a simple, intuitive mobile UI and a smooth onboarding flow.",
    url: "https://play.google.com/store/search?q=shoftek&c=apps",
  },
];

// More design work (Behance + concepts)
export const BEHANCE_PROJECTS = [
  {
    title: "MusicGrid",
    tag: "Music SaaS",
    desc: "A vibrant landing page concept for a custom-music studio, with clear pricing tiers and a gradient-led brand.",
    image:
      "https://customer-assets-lqy194kg.emergentagent.net/job_outcome-focused-1/artifacts/r42x0hlo_music-grid.webp",
    url: "https://www.behance.net/rickysokhi",
  },
  {
    title: "Rezilla",
    tag: "Real Estate",
    desc: "A polished real estate property-search concept with prominent search and rich listing cards.",
    image:
      "https://customer-assets-lqy194kg.emergentagent.net/job_outcome-focused-1/artifacts/19z620ds_Rezilla.webp",
    url: "https://www.behance.net/rickysokhi",
  },
  {
    title: "Trendiin",
    tag: "Social App",
    desc: "A launch & marketing site concept for a new social platform, built to drive app downloads.",
    image:
      "https://customer-assets-lqy194kg.emergentagent.net/job_outcome-focused-1/artifacts/a7qpossv_trendiin.webp",
    url: "https://www.behance.net/rickysokhi",
  },
  {
    title: "Thermo Star",
    tag: "Product / Brand",
    desc: "A crisp product-brand site for a heating & cooling company, balancing technical detail with clear visuals.",
    image: "/portfolio/thermo-star.jpg",
    url: "https://www.behance.net/gallery/77023031/Thermo-Star",
  },
  {
    title: "Lawyers",
    tag: "Law Firm",
    desc: "A trustworthy, professional website for a law practice with clear service areas and consultation CTAs.",
    image: "/portfolio/lawyers.jpg",
    url: "https://www.behance.net/gallery/70033551/LAWYERS",
  },
  {
    title: "Finance Advisor",
    tag: "Finance",
    desc: "A confident financial-advisory site designed to explain services simply and generate qualified leads.",
    image: "/portfolio/finance-advisor.jpg",
    url: "https://www.behance.net/gallery/70033411/Finance-Advisor",
  },
  {
    title: "Beauty Shop",
    tag: "E-commerce",
    desc: "An elegant beauty e-commerce store with a soft palette and a smooth, shoppable product experience.",
    image: "/portfolio/beauty-shop.jpg",
    url: "https://www.behance.net/gallery/70033321/Beauty-Shop",
  },
  {
    title: "Florist",
    tag: "E-commerce",
    desc: "A fresh, inviting florist storefront with a warm, tactile feel and an easy path to purchase.",
    image: "/portfolio/florist.jpg",
    url: "https://www.behance.net/gallery/70033489/FLORIST",
  },
];

export const SERVICES = [
  {
    icon: "PenTool",
    title: "UI / UX design",
    who: "For founders and teams who need intuitive, beautiful interfaces users love.",
    solves: "Confusing flows, dated visuals, and designs that don't convert.",
    outcome: "Clean, on-brand UI/UX — from wireframes to a polished design system.",
  },
  {
    icon: "Code2",
    title: "Website & front-end development",
    who: "For businesses that need their design built into a fast, real website.",
    solves: "Slow, unresponsive sites and designs that never make it to code.",
    outcome: "Hand-coded, responsive front-ends that look sharp on every device.",
  },
  {
    icon: "LayoutGrid",
    title: "Landing pages & web apps",
    who: "For startups launching a product, campaign, or SaaS.",
    solves: "Generic templates and pages that fail to communicate or convert.",
    outcome: "High-impact landing pages and web apps built to drive action.",
  },
  {
    icon: "Sparkles",
    title: "AI integration & growth",
    who: "For teams who want AI features and a partner focused on outcomes.",
    solves: "Busywork, missing AI features, and decisions made on gut feel.",
    outcome: "Smart AI touches and data-driven improvements to conversion.",
  },
];

export const EXPERIENCE = [
  {
    company: "Freelance (Independent)",
    role: "UI/UX Designer & Front-end Developer",
    points: [
      "Design and build websites, landing pages and web apps for clients worldwide.",
      "Own the full journey — UX, UI, hand-coded front-end and launch.",
      "Use an AI-accelerated workflow to deliver faster without cutting quality.",
      "Partner with founders on conversion, clarity and growth.",
    ],
  },
  {
    company: "Graphixtation",
    role: "UI/UX Designer & Sr. Front-end Developer",
    points: [
      "Designed and developed responsive websites for domestic & international clients.",
      "Turned PSDs into pixel-perfect, W3C-valid HTML/CSS.",
      "Built interactive front-ends with JavaScript and jQuery.",
      "Established front-end practices that improved quality and speed.",
    ],
  },
  {
    company: "Milagro Interactive Technologies",
    role: "Project Lead & UI/UX",
    points: [
      "Led project delivery while shaping UI/UX direction.",
      "Managed client communication and translated needs into scope.",
      "Coordinated cross-functional teams to meet delivery timelines.",
      "Championed quality and consistency across product surfaces.",
    ],
  },
  {
    company: "Dataguise",
    role: "Senior Engineer / Scrum Master / UI Team",
    points: [
      "Contributed to UI engineering while serving as Scrum Master.",
      "Facilitated planning, reviews and retrospectives.",
      "Bridged engineering and design to clarify scope and priorities.",
      "Improved throughput through better estimation and focus.",
    ],
  },
  {
    company: "IntelERA & Appscrip",
    role: "Project / Account Manager — Digital",
    points: [
      "Managed delivery of SaaS, web and mobile products end to end.",
      "Ran Agile ceremonies and owned client relationships.",
      "Aligned scope, budget and expectations across stakeholders.",
      "Drove process improvements for predictable delivery.",
    ],
  },
];
